import {
  ActivityServiceEvent,
  AutoplayServiceEvent,
  BetServiceEvent,
  ClockServiceEvent,
  DialogServiceEvent,
  FreeRoundsServiceEvent,
  FreeSpinsServiceEvent,
  GameInformationServiceEvent,
  GameRoundServiceEvent,
  GameRulesServiceEvent,
  GameServiceEvent,
  GameSettingsServiceEvent,
  IGameService,
  IService,
  IServices,
  MoneyServiceEvent,
  PaytableServiceEvent,
  PluginMenuServiceEvent,
  SpinServiceEvent,
  TickerServiceEvent,
  WinServiceEvent
} from "@netent/origin";
import {
  DisplayMode,
  IBetState,
  IOptions,
  IThemeState,
  ITotalWinState,
  PlayUI,
  SpinButtonPosition,
  Target,
  View
} from "@netent/playui";
import { Store } from "redux";
import {
  DEFAULT_GAME_PRODUCER,
  PLAY_FOR_FUN_HIDE_DURATION,
  PLAY_FOR_FUN_SHOW_DURATION
} from "./constants";
import {
  getLanguage,
  setPlayForFunIndicatorEnabled,
  updateView
} from "./helpers";
import { initState } from "./init";
import * as origin from "./origin";
import * as Listeners from "./playui";
import { onViewChangeRequest } from "./playui/onViewChangeRequest";
import { actions, createStore, IStoreAction, IStoreState } from "./store";
import { ListenerCreator } from "./types";
import { toggle } from "./utils";

export interface IAdapterSetting {
  key: string;
  name: string;
  available?: boolean;
}

export interface IAdapterConfig {
  gameName: string;
  gameProducer?: string;
  settings: IAdapterSetting[];
  containers: {
    keypad: HTMLElement;
    dialog: HTMLElement;
  };
}

export class Adapter {
  private static isDesktop(game: IGameService) {
    return game.getCurrentPlatform() === "desktop";
  }

  private playui: PlayUI;
  private config: IAdapterConfig;
  private services: IServices;
  private store: Store<IStoreState, IStoreAction> = createStore();

  constructor(services: IServices, config: IAdapterConfig) {
    this.services = services;
    this.config = { gameProducer: DEFAULT_GAME_PRODUCER, ...config };

    this.store.dispatch(actions.updateGameSettings(this.config.settings));
  }

  public render(): void {
    const options = this.getPlayUIOptions();

    this.playui = new PlayUI(options);

    this.store.subscribe(() => {
      this.playui.setState(updateView(this.store));
    });

    this.setupPlayForFunIndicator();
    this.setupOriginEvents();
    this.setupPlayUIEvents();
  }

  public show(): void {
    this.store.dispatch(actions.updateView(View.DEFAULT));
  }

  public hide(): void {
    this.store.dispatch(actions.updateView(null));
  }

  public setDisplayMode(displayMode: DisplayMode): void {
    this.playui.setState({ displayMode });
  }

  public setTheme(theme: IThemeState): void {
    this.playui.setState({ theme });
  }

  /**
   * @deprecated since version 2.0.0
   */
  public UNSAFE_setTotalWin(totalWin: ITotalWinState): void {
    this.playui.setState({ totalWin });
  }

  /**
   * @deprecated since version 2.0.0
   */
  public UNSAFE_setBet(bet: IBetState): void {
    this.playui.setState({ bet });
  }

  /**
   * @deprecated since version 2.0.0
   */
  public UNSAFE_setSpinButtonState(enabled: boolean): void {
    this.playui.setState({ spin: { enabled } });
  }

  /**
   * @deprecated since version 2.0.0
   */
  public UNSAFE_setSpinButtonPosition(
    buttonPosition: SpinButtonPosition
  ): void {
    this.playui.setState({ spin: { buttonPosition } });
  }

  /**
   * @deprecated since version 2.0.0
   */
  public set UNSAFE_onSpinRequest(callback: VoidFunction) {
    this.playui.onSpinRequest = callback;
  }

  private getPlayUIOptions(): IOptions {
    const { containers } = this.config;

    return {
      target: Adapter.isDesktop(this.services.game)
        ? Target.DESKTOP
        : Target.MOBILE,
      language: getLanguage(this.services),
      state: initState(this.services, this.config, this.store),
      containers
    };
  }

  private setupPlayForFunIndicator(): void {
    if (!this.services.game.isPlayForFun()) {
      return;
    }

    toggle({
      show: () => this.playui.setState(setPlayForFunIndicatorEnabled(true)),
      hide: () => this.playui.setState(setPlayForFunIndicatorEnabled(false)),
      showDuration: PLAY_FOR_FUN_SHOW_DURATION,
      hideDuration: PLAY_FOR_FUN_HIDE_DURATION
    });
  }

  private setupListeners<T>(
    service: IService<T>,
    listeners: Map<T, ListenerCreator>
  ) {
    listeners.forEach((transformer, event) =>
      service.on(event, (...args: any[]) => {
        const state = transformer(this.services, this.store)(...args);
        const { view } = state;

        if (view) {
          onViewChangeRequest(this.services, this.store)(view);
        }

        this.playui.setState(state);
      })
    );
  }

  private setupOriginEvents(): void {
    const {
      activity,
      autoplay,
      bet,
      clock,
      dialog,
      freeRounds,
      freeSpins,
      game,
      gameInformation,
      gameRound,
      gameRules,
      gameSettings,
      money,
      paytable,
      pluginMenu,
      spin,
      ticker,
      win
    } = this.services;

    this.setupListeners<ActivityServiceEvent>(
      activity,
      origin.activity.listeners
    );
    this.setupListeners<AutoplayServiceEvent>(
      autoplay,
      origin.autoplay.listeners
    );
    this.setupListeners<BetServiceEvent>(bet, origin.bet.listeners);
    this.setupListeners<ClockServiceEvent>(clock, origin.clock.listeners);
    this.setupListeners<DialogServiceEvent>(dialog, origin.dialog.listeners);
    this.setupListeners<FreeRoundsServiceEvent>(
      freeRounds,
      origin.freeRounds.listeners
    );
    this.setupListeners<FreeSpinsServiceEvent>(
      freeSpins,
      origin.freeSpins.listeners
    );
    this.setupListeners<GameInformationServiceEvent>(
      gameInformation,
      origin.gameInformation.listeners
    );
    this.setupListeners<GameRoundServiceEvent>(
      gameRound,
      origin.gameRound.listeners
    );
    this.setupListeners<GameRulesServiceEvent>(
      gameRules,
      origin.gameRules.listeners
    );
    this.setupListeners<GameServiceEvent>(game, origin.game.listeners);
    this.setupListeners<GameSettingsServiceEvent>(
      gameSettings,
      origin.gameSettings.listeners
    );
    this.setupListeners<MoneyServiceEvent>(money, origin.money.listeners);
    this.setupListeners<PaytableServiceEvent>(
      paytable,
      origin.paytable.listeners
    );
    this.setupListeners<PluginMenuServiceEvent>(
      pluginMenu,
      origin.pluginMenu.listeners
    );
    this.setupListeners<SpinServiceEvent>(spin, origin.spin.listeners);
    this.setupListeners<TickerServiceEvent>(ticker, origin.ticker.listeners);
    this.setupListeners<WinServiceEvent>(win, origin.win.listeners);
  }

  private setupPlayUIEvents() {
    Listeners.setupListeners(this.services, this.store, this.playui);
  }
}
